import { Reference, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import BaseErrors from "@components/fields/BaseErrors";
import Checkbox from "@components/fields/Checkbox";
import DataSelect from "@components/fields/DataSelect";
import Error from "@components/fields/Error";
import PhoneInput from "@components/fields/PhoneInput";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import RichTextarea from "@components/fields/RichTextarea";
import Form from "@components/forms/Form";
import InviteAccountMembersForm from "@components/forms/InviteAccountMembersForm";
import PhoneNumberForm from "@components/forms/PhoneNumberForm";
import Modal from "@components/Modal";
import ProgressCircle from "@components/ProgressCircle";
import styles from "@components/schedule/ScheduleSession.module.css";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import Heading4 from "@components/typography/Heading4";
import useGetMyTimeWithTimezoneAbbreviation from "@hooks/useGetMyTimeWithTimezoneAbbreviation";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import {
  ConsultFragment as Consult,
  ConsultAvailabilityFragment,
  ConsultCreateDocument,
  ConsultCreateMutation,
  ConsultCreateMutationVariables,
  ConsultDurationEnum,
  ConsultFragmentDoc,
  ContactMethodEnum,
  ExpertRoleRoleEnum,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { imageUrl } from "@utils/imageUtils";
import { getCareProfileName } from "@utils/modelUtils";

type FormData = {
  inviteEmails: string[];
  invitedClientIds: string[];
  languageCode: string | null;
  languageRequest: boolean;
  notes: string | null;
  phoneNumber: string | null;
  smsReminders: boolean | null;
};

type Props = {
  availability: ConsultAvailabilityFragment;
  contactMethod: ContactMethodEnum;
  duration: ConsultDurationEnum;
  handleEditBooking: () => void;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
  onSuccess: (consult: Consult) => void;
};

const ConfirmProgressCircle = ({ duration, size }: { duration: ConsultDurationEnum; size: number }) => (
  <ProgressCircle
    percents={
      duration === ConsultDurationEnum.FIFTEEN_MINUTES ? 25 : duration === ConsultDurationEnum.THIRTY_MINUTES ? 50 : 100
    }
    size={size}
  >
    {size !== 70 && (
      <>
        <div className={styles.durationNumber}>
          <Heading1>
            <FormattedNumber
              value={
                duration === ConsultDurationEnum.FIFTEEN_MINUTES
                  ? 15
                  : duration === ConsultDurationEnum.THIRTY_MINUTES
                    ? 30
                    : 60
              }
            />
          </Heading1>
        </div>
        <BodyCopy color="primary">
          <FormattedMessage id="pages.consults.duration.minutes" />
        </BodyCopy>
      </>
    )}
  </ProgressCircle>
);

const ConfirmForm = ({ availability, contactMethod, duration, handleEditBooking, journey, onSuccess }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const phoneNumberOpener = useOpener();
  const phoneNumberRef = useRef<HTMLDivElement>(null);
  const inviteOthersOpener = useOpener();
  const getMyTimeWithTimezoneAbbreviation = useGetMyTimeWithTimezoneAbbreviation();

  const initialPhoneNumber = useMemo(() => {
    if (myClient) {
      return myClient.consultPhoneNumber ?? myClient.verificationPhoneNumber;
    }
    return journey.owner.consultPhoneNumber ?? journey.owner.verificationPhoneNumber;
  }, [journey.owner.consultPhoneNumber, journey.owner.verificationPhoneNumber, myClient]);

  const currentClientRole = useMemo(
    () =>
      journey.clientRoles.find(role =>
        myClient ? role.client.id === myClient.id : role.client.id === journey.owner.id
      ),
    [journey.clientRoles, journey.owner.id, myClient]
  );

  const defaultValues: FormData = {
    inviteEmails: [],
    invitedClientIds: currentClientRole ? [currentClientRole.client.id] : [],
    languageCode: journey.careProfile.languageCode ?? null,
    languageRequest: journey.careProfile.languageCode ? true : false,
    notes: "",
    phoneNumber: initialPhoneNumber?.number ?? null,
    smsReminders: contactMethod === ContactMethodEnum.PHONE
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createConsult, { client }] = useMutation<ConsultCreateMutation, ConsultCreateMutationVariables>(
    ConsultCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.consultCreate?.consult) {
          if (
            !journey.expertRoles?.some(
              role =>
                role.expert?.id === data.consultCreate?.consult?.expert.id &&
                [ExpertRoleRoleEnum.PRIMARY, ExpertRoleRoleEnum.TEAM_MEMBER].includes(role.role)
            )
          ) {
            client.resetStore();
          }
          store.modify({
            fields: {
              consults(cachedConsultRefs: readonly Reference[]) {
                const newConsultRef = store.writeFragment({
                  data: data.consultCreate?.consult,
                  fragment: ConsultFragmentDoc,
                  fragmentName: "Consult"
                });
                if (newConsultRef && data?.consultCreate?.consult) {
                  return [...cachedConsultRefs, newConsultRef];
                }
                return [...cachedConsultRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      inviteEmails: yup.array().defined().of(yup.string().defined()),
      invitedClientIds: yup.array().defined().of(yup.string().defined()),
      languageCode: yup.string().nullable().defined(),
      languageRequest: yup.boolean().defined(),
      notes: yup.string().nullable().defined(),
      phoneNumber: yup.string().nullable().defined(),
      smsReminders: yup.boolean().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const phoneNumber = form.watch("phoneNumber");
  const inviteEmails = form.watch("inviteEmails");
  const invitedClientIds = form.watch("invitedClientIds");
  const smsReminders = form.watch("smsReminders");
  const languageCode = form.watch("languageCode");
  const languageRequest = form.watch("languageRequest");

  const inviteeOptions = useMemo(() => {
    const otherClientRoles = journey.clientRoles.filter(role => role.client.id !== myClient?.id);

    return otherClientRoles.map(option => ({
      label: option.client.fullName,
      value: option.client.id
    }));
  }, [journey.clientRoles, myClient?.id]);

  const invitedClients = useMemo(
    () =>
      journey.clientRoles.filter(role => invitedClientIds.some(id => role.client.id === id)).map(role => role.client),
    [invitedClientIds, journey.clientRoles]
  );

  const handlePhoneNumberAdded = (number?: string) => {
    phoneNumberOpener.close();
    form.setValue("phoneNumber", number ?? null, { shouldValidate: true });
  };

  const isValid = useMemo(() => {
    if (phoneNumber && phoneNumber !== "") {
      if (isValidPhoneNumber(phoneNumber)) {
        form.clearErrors("phoneNumber");
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, [form, phoneNumber]);

  useEffect(() => {
    if (languageRequest && !languageCode) {
      form.setValue("languageCode", journey.careProfile.languageCode);
    } else if (!languageRequest) {
      form.setValue("languageCode", null);
    }
  }, [form, journey.careProfile.languageCode, languageCode, languageRequest]);

  const onSubmit = useCallback(async () => {
    if ((contactMethod === ContactMethodEnum.PHONE || form.getValues().smsReminders) && !phoneNumber) {
      form.setError("phoneNumber", {
        message: intl.formatMessage({ id: `errors.consult.phoneNumber.blank` }),
        type: "validate"
      });
      phoneNumberRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (journey.primaryExpert) {
      const emailInvitessForConsult = inviteEmails.filter(
        email => !journey.careTeamInvites?.some(invite => invite.email === email)
      );

      return createConsult({
        variables: {
          availability: {
            duration: availability.duration,
            expertId: availability.expert.id,
            startTime: availability.startTime
          },
          contactMethod,
          formattedNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : null,
          invitedClientIds: form.getValues().invitedClientIds ?? journey.clientRoles.map(role => role.client.id),
          invitedEmails: emailInvitessForConsult,
          journeyId: journey.id,
          languageCode,
          notes: form.getValues().notes,
          number: phoneNumber,
          smsReminders
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultCreate,
          extraBaseFields: ["email"],
          intl,
          onSuccess: () => {
            if (response.data?.consultCreate?.consult) {
              onSuccess(response.data?.consultCreate?.consult);
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.consult"
        });
      });
    }
  }, [
    availability.duration,
    availability.expert.id,
    availability.startTime,
    contactMethod,
    createConsult,
    form,
    intl,
    inviteEmails,
    journey.careTeamInvites,
    journey.clientRoles,
    journey.id,
    journey.primaryExpert,
    languageCode,
    onSuccess,
    phoneNumber,
    smsReminders
  ]);

  return (
    <>
      <Form data-test="consult-schedule-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
        <div className={styles.confirmContainer}>
          <div className={styles.confirmHeader}>
            <Heading1>
              <FormattedMessage id="pages.consults.confirm.title" />
            </Heading1>
          </div>
          <div className={styles.confirmBox}>
            <div className={styles.flexWrap}>
              <Heading2>
                <FormattedMessage
                  id="pages.consults.confirm.regardingCare"
                  values={{
                    name: getCareProfileName({ intl, journey })
                  }}
                />
              </Heading2>
              <div className={styles.startTimePill}>
                <BodyCopy color="primary">
                  <DateAndTimeStamp value={availability.startTime} />
                </BodyCopy>
              </div>
            </div>
            <div className={styles.infoBoxes}>
              <div className={styles.infoBox}>
                <ExpertAvatar expert={availability.expert} size={148} />
                <div className={styles.mobileLeftAlign}>
                  <div className={styles.infoBoxTitle}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id="pages.consults.confirm.expert" />
                    </BodyCopy>
                  </div>
                  <BodyCopy color="primary">{availability.expert.firstName}</BodyCopy>
                </div>
              </div>
              <div className={styles.infoBox}>
                <div className={styles.mobileHidden}>
                  <ConfirmProgressCircle duration={duration} size={148} />
                </div>
                <div className={styles.mobileShow}>
                  <ConfirmProgressCircle duration={duration} size={70} />
                </div>
                <div className={styles.mobileLeftAlign}>
                  <div className={styles.infoBoxTitle}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id="pages.consults.confirm.sessionLength" />
                    </BodyCopy>
                  </div>
                  <BodyCopy color="primary">
                    <FormattedMessage id={`models.duration.${duration}`} />
                  </BodyCopy>
                </div>
              </div>
              <div className={styles.infoBox}>
                <div>
                  <div className={styles.confirmDateAndTime}>
                    <BodyCopy color="white">
                      <FormattedDate value={availability.startTime} weekday="short" />
                    </BodyCopy>
                    <div className={styles.confirmDay}>
                      <Heading1 color="white">
                        <FormattedDate day="numeric" value={availability.startTime} />
                      </Heading1>
                    </div>
                    <div className={styles.mobileHidden}>
                      <BodyCopy color="white">{getMyTimeWithTimezoneAbbreviation(availability.startTime)}</BodyCopy>
                    </div>
                  </div>
                </div>

                <div className={styles.mobileLeftAlign}>
                  <div className={styles.infoBoxTitle}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id="pages.consults.confirm.dateAndTime" />
                    </BodyCopy>
                  </div>
                  <BodyCopy bold color="primary">
                    <DateAndTimeStamp mobileTimeOnNewLine value={availability.startTime} />
                  </BodyCopy>
                </div>
              </div>
              <div className={styles.infoBox}>
                {contactMethod === ContactMethodEnum.PHONE ? (
                  <>
                    <div className={styles.infoBoxPhoneContainer} ref={phoneNumberRef}>
                      <Icon color="white" icon="phone" size="xlarge" />
                    </div>
                    <div className={styles.mobileLeftAlign}>
                      <div className={styles.infoBoxTitle}>
                        <BodyCopy bold color="primary">
                          <FormattedMessage id="pages.consults.confirm.byPhone" />
                        </BodyCopy>
                        <Icon
                          color="primary"
                          hoverContent={intl.formatMessage({
                            id: `pages.consults.contactMethod.description.${contactMethod}`
                          })}
                          icon="questionCircle"
                          spacingStart
                        />
                      </div>
                      <div className={form.formState.errors.phoneNumber ? styles.errorBox : ""}>
                        <div className={styles.phoneAndEdit}>
                          <BodyCopy color="primary">{phoneNumber ? formatPhoneNumber(phoneNumber) : ""}</BodyCopy>
                          <CircleButton
                            ariaLabelKey="accessibility.ariaLabels.edit"
                            fill="clear"
                            icon="pencil"
                            onClick={phoneNumberOpener.open}
                            size="medium"
                          />
                          <RequiredFieldIndicator />
                        </div>
                        {form.formState.errors.phoneNumber?.message && (
                          <div>
                            <Error error={form.formState.errors.phoneNumber} />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <img
                      aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.zoom" })}
                      className={styles.infoBoxZoom}
                      src={imageUrl("zoom")}
                    />
                    <div className={styles.mobileLeftAlign}>
                      <div className={styles.infoBoxTitle}>
                        <BodyCopy bold color="primary">
                          <FormattedMessage id="pages.consults.confirm.contactMethod" />
                        </BodyCopy>
                      </div>
                      <BodyCopy color="primary">
                        <FormattedMessage id="pages.consults.confirm.zoom" />
                      </BodyCopy>
                      <Icon
                        color="primary"
                        hoverContent={intl.formatMessage({
                          id: `pages.consults.contactMethod.description.${contactMethod}`
                        })}
                        icon="questionCircle"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.confirmSection}>
              <div className={styles.spacingBottomLarge}>
                <Heading2>
                  <FormattedMessage id="pages.consults.confirm.attendees" />
                </Heading2>
              </div>
              <div className={styles.attendees}>
                {invitedClients.map(client => (
                  <div className={styles.attendee} key={client.id}>
                    <div className={styles.flex}>
                      <BodyCopy color="gray">{client.fullName}</BodyCopy>
                      {currentClientRole?.client.id !== client.id && (
                        <div className={styles.tinySpacingLeft}>
                          <CircleButton
                            ariaLabelKey="accessibility.ariaLabels.delete"
                            fill="clear"
                            icon="trash"
                            onClick={() =>
                              form.setValue(
                                "invitedClientIds",
                                invitedClientIds.filter(value => value !== client.id)
                              )
                            }
                            size="medium"
                          />
                        </div>
                      )}
                    </div>
                    <div className={styles.tinySpacing}>
                      <Heading4 color="gray-500">{client.email.email}</Heading4>
                    </div>
                  </div>
                ))}
                {inviteEmails.map(email => (
                  <div className={styles.attendee} key={email}>
                    <div className={styles.flex}>
                      <BodyCopy color="gray">{email}</BodyCopy>
                      <div className={styles.tinySpacingLeft}>
                        <CircleButton
                          ariaLabelKey="accessibility.ariaLabels.delete"
                          fill="clear"
                          icon="trash"
                          onClick={() =>
                            form.setValue(
                              "inviteEmails",
                              inviteEmails.filter(value => value !== email)
                            )
                          }
                          size="medium"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button fill="clear" onClick={inviteOthersOpener.open}>
                  <IconBase icon="add" slot="start" />
                  <FormattedMessage
                    id={
                      inviteEmails.length === 0 && invitedClients.length === 1
                        ? "forms.consult.addInvites"
                        : "forms.consult.addOtherInvites"
                    }
                  />
                </Button>
              </div>
              {form.formState.errors.invitedClientIds && (
                <div className={styles.error}>
                  <FormattedMessage id="errors.consult.invitee" />
                </div>
              )}
            </div>
            <div className={styles.confirmSection}>
              <div className={styles.spacingBottom}>
                <Heading2>
                  <FormattedMessage id="pages.consults.confirm.discuss" />
                </Heading2>
              </div>
              <div className={styles.spacingBottomLarge}>
                <BodyCopy color="primary">
                  <FormattedMessage id="pages.consults.confirm.optional" />
                </BodyCopy>
              </div>
              <RichTextarea<FormData>
                controls={[]}
                error={form.formState.errors.notes}
                form={form}
                journeyId={journey.id}
                name="notes"
                placeholder={intl.formatMessage({ id: "dictionary.typeHere" })}
                required
              />
            </div>

            <div>
              <Checkbox<FormData>
                control={form.control}
                error={form.formState.errors.languageRequest}
                name="languageRequest"
              >
                <FormattedMessage id="pages.client.consults.requestLanguageSupport" />
              </Checkbox>
            </div>
            <div className={styles.spacingBottom}>
              <Heading4 color="gray-500">
                <FormattedMessage id="pages.client.consults.offersInterpretation" />
              </Heading4>
            </div>
            <div className={languageRequest ? styles.spacingBottom : "ion-hide"}>
              <DataSelect<FormData>
                control={form.control}
                error={form.formState.errors.languageCode}
                hasNoneOption
                label=""
                name="languageCode"
                type="languages"
              />
            </div>

            <Checkbox<FormData> control={form.control} error={form.formState.errors.smsReminders} name="smsReminders">
              <FormattedMessage id="pages.consults.confirm.smsReminders" />
            </Checkbox>
            <div className={styles.spacingBottom}>
              <Heading4 color="gray-500">
                <FormattedMessage id="pages.consults.smsRemindersDescription" />
              </Heading4>
            </div>
            <div className={contactMethod === ContactMethodEnum.ZOOM && smsReminders ? "" : "ion-hide"}>
              <PhoneInput<FormData>
                control={form.control}
                error={form.formState.errors.phoneNumber}
                name="phoneNumber"
              />
            </div>

            <div className={styles.scheduleButtons}>
              <ButtonLink onClick={handleEditBooking}>
                <FormattedMessage id="pages.consults.confirm.editBooking" />
              </ButtonLink>
              <Button
                data-test="submit-button"
                disabled={!form?.formState.isValid || form?.formState.isSubmitting || !isValid}
                size="large"
                type="submit"
              >
                <FormattedMessage id="pages.consults.confirm.bookSession" />
              </Button>
            </div>
            <div className="align-center">
              <BaseErrors errors={baseErrors} />
            </div>
          </div>
        </div>
      </Form>
      <Modal opener={phoneNumberOpener} title={intl.formatMessage({ id: "pages.consults.confirm.editPhoneNumber" })}>
        {() => (
          <PhoneNumberForm
            forConsults
            initialValue={phoneNumber}
            onCancel={phoneNumberOpener.close}
            onSuccess={handlePhoneNumberAdded}
          />
        )}
      </Modal>
      <Modal opener={phoneNumberOpener} title={intl.formatMessage({ id: "pages.consults.confirm.sessionPhoneNumber" })}>
        {() => (
          <PhoneNumberForm
            forConsults
            initialValue={phoneNumber}
            onCancel={phoneNumberOpener.close}
            onSuccess={handlePhoneNumberAdded}
          />
        )}
      </Modal>
      <Modal
        opener={inviteOthersOpener}
        title={intl.formatMessage({ id: "models.client.inviteAccountMembers.consultTitle" })}
      >
        {({ modalDisplayed }) => (
          <InviteAccountMembersForm
            autofocus={modalDisplayed}
            consultScheduling={{
              inviteOptions: inviteeOptions,
              selectedClientIds: invitedClientIds
            }}
            journey={journey}
            onCancel={inviteOthersOpener.close}
            onSuccess={(emails, clientIds) => {
              const newInviteEmails = [...inviteEmails].concat(emails ?? []);
              form.setValue(
                "inviteEmails",
                newInviteEmails.filter((v, i) => newInviteEmails.indexOf(v) === i)
              );
              if (clientIds) {
                const newClientIds = [...invitedClientIds].concat(clientIds);
                form.setValue(
                  "invitedClientIds",
                  newClientIds.filter((v, i) => newClientIds.indexOf(v) === i)
                );
              }
              inviteOthersOpener.close();
            }}
            saveKey="dictionary.add"
          />
        )}
      </Modal>
    </>
  );
};

export default ConfirmForm;
